import { CloseIcon, EditIcon, SearchIcon, ViewIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    DrawerBody,
    DrawerHeader,
    Flex,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Link,
    Text,
    VStack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppDatePicker from "components/AppDatePicker";
import AppSelect from "components/AppSelect";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useUserRole } from "hooks/useUserRole";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  reference: string;
  tax_nature_name: string;
  tax_payer_name: string;
  due_date: string;
  amount: undefined;
  status: string;
  institution: string;
  tax_nature: string;
  sub_area: string;
  tax_frequency: string;
  rate: number;
  city: string;
  paid_by: string;
};

export default function TaxAssessmentManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const { hasPermission } = useAuthentication();
  const userRole = useUserRole();

  // Fonction pour formater la date d'échéance
  const formatDueDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) return dateString;
      
      // Formater en format français: JJ-MM-AAAA
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };
  const [page, setPage] = useState(1);
  const [institution, setInstitution] = useState<string>("");
  const [subArea, setSubArea] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [pageSize, setPageSize] = useState(25);
  const [taxNature, setTaxNature] = useState<string>("");
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const {
    getTaxAssessments,
    getInstitutions,
    getCities,
    getSubAreas,
    getTaxNatures,
    deleteTaxAssessment,
  } = useApi();
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(),
    new Date(),
  ]);
  const { data: taxNatures, isLoading: isTaxNatureLoading } = useCache(
    "taxNatures",
    () => getTaxNatures(1, "", institution, "")
  );
  const { data: tax_assessments, mutate } = useCache("tax_assessments", () =>
    getTaxAssessments(
      page,
      "",
      searchBarValue34,
      50,
      institution,
      city,
      subArea,
      "",
      "",
      "",
      selectedDates[0]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      selectedDates[1]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      taxNature
    )
  );
  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, institution, ""));
  const {
    data: subAreas,
    isLoading: isSubAreaLoading,
    mutate: mutateSubArea,
  } = useCache("subAreas", () => getSubAreas(1, subArea, "", institution));
  const {
    data: cities,
    isLoading: isCityLoading,
    mutate: mutateCity,
  } = useCache("cities", () => getCities("", 1, "", city));
  const handleSearch = (event: any) => {
    const value = event.target.value;
    mutate(getTaxAssessments(page, value));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTaxAssessments(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTaxAssessments(pg));
  };
  const handleSetNextPage = () => {
    if (
      tax_assessments.length > 0 &&
      tax_assessments[0].num_of_pages >= page + 1
    ) {
      setPage((prev: any) => prev + 1);
      mutate(getTaxAssessments(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (tax_assessments.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getTaxAssessments(page + 1));
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("tax_payer_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Débiteur</Heading>
          </Flex>
        ),
        meta: { width: "192px" },
      }),
      table3ColumnHelper.accessor("institution", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Institution</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("sub_area", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Zone</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("city", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Ville</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("tax_nature_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Nature de règlement</Heading>
          </Flex>
        ),
        meta: { width: "170px" },
      }),
      table3ColumnHelper.accessor("paid_by", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Payé par</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("rate", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{`${info.getValue<string>()}%`}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Taux autorisé</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("amount", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Montant de la créance</Heading>
          </Flex>
        ),
        meta: { width: "170px" },
      }),
      table3ColumnHelper.accessor("status", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>
              {info.getValue<string>() === "paid" ? "payé" : "pas à jour"}
            </Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Statut</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("tax_frequency", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Périodicité</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("due_date", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{formatDueDate(info.getValue<string>())}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Date d'échéance</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("taxassessment", "get") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<ViewIcon />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            )}
            {hasPermission("taxassessment", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex width={"100%"} justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [handleViewItem, hasPermission, isDeleting, activeItem, onConfirmOpen]);
  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Imputation de créance
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              {hasPermission("taxassessment", "create") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={
                    <Image
                      src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                      alt="Handshake-deal Svgrepo.com"
                    />
                  }
                  fontWeight={700}
                  gap="8px"
                  minW="128px"
                  borderRadius="8px"
                  as={Link}
                  href="/tax-assessments/add"
                >
                  Affecter la nature de règlement
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              // flex={1}
              alignItems="center"
              // alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
              gap={1}
            >
              <AppDatePicker
                selectedDates={selectedDates}
                onDateChange={(date) => {
                  setSelectedDates(date);
                  mutate(
                    getTaxAssessments(
                      page,
                      "",
                      searchBarValue34,
                      50,
                      institution,
                      city,
                      subArea,
                      "",
                      "",
                      "",
                      selectedDates[0]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      selectedDates[1]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      taxNature
                    )
                  );
                }}
              />
              {!isInstitutionLoading &&
                userRole === TAX_SUPER_ADMIN && (
                  <Box width={"200px"}>
                    <AppSelect
                     width="200px"
                      height={"40px"}
                      id="institution"
                      name="institution"
                      onChange={(field: string, value: any) => {
                        console.log(institution);
                        if (!value) {
                          mutate(
                            getTaxAssessments(
                              page,
                              "",
                              searchBarValue34,
                              50,
                              value,
                              city,
                              subArea,
                              "",
                              "",
                              "",
                              selectedDates[0]
                                ?.toISOString()
                                .split("T")[0]
                                .split("-")
                                .reverse()
                                .join("-"),
                              selectedDates[1]
                                ?.toISOString()
                                .split("T")[0]
                                .split("-")
                                .reverse()
                                .join("-"),
                              taxNature
                            )
                          );
                        }
                        setInstitution(value);
                        mutateInstitution(
                          getInstitutions(1, +(institution || 0), value)
                        );
                      }}
                      value={institution as string}
                      placeholder="Sélectionner une institution"
                      options={institutions}
                      isMulti={false}
                      isLoading={isInstitutionLoading}
                    />
                  </Box>
                )}
              
                {!isTaxNatureLoading && (
                   <Box width={"200px"}>

                     <AppSelect
                      width="200px"
                       height={"40px"}
                       id="taxNature"
                       name="taxNature"
                       onChange={(field: string, value: any) => {
                         setTaxNature(value);
                         mutate(
                           getTaxAssessments(
                             page,
                             "",
                             searchBarValue34,
                             50,
                             institution,
                             city,
                             subArea,
                             "",
                             "",
                             "",
                             selectedDates[0]
                               ?.toISOString()
                               .split("T")[0]
                               .split("-")
                               .reverse()
                               .join("-"),
                             selectedDates[1]
                               ?.toISOString()
                               .split("T")[0]
                               .split("-")
                               .reverse()
                               .join("-"),
                             value
                           )
                         );
                       }}
                       value={taxNature as string}
                       placeholder="Sélectionner nature de règlement"
                       options={taxNatures}
                       isMulti={false}
                       isLoading={isTaxNatureLoading}
                     />
                   </Box>
                )}
              {!isCityLoading && userRole === TAX_SUPER_ADMIN && (
                <Box width={"200px"}>

                  <AppSelect
                  width="200px"
                    height={"40px"}
                    id="city"
                    name="city"
                    onChange={(field: string, value: any) => {
                      if (!value) {
                        mutate(
                          getTaxAssessments(
                            page,
                            "",
                            searchBarValue34,
                            50,
                            institution,
                            value,
                            subArea,
                            "",
                            "",
                            "",
                            selectedDates[0]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            selectedDates[1]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            taxNature
                          )
                        );
                      }
                      setCity(value);
                      mutateCity(getCities("", 1, "", value));
                    }}
                    value={city as string}
                    placeholder="Sélectionner une ville"
                    options={cities}
                    isMulti={false}
                    isLoading={isCityLoading}
                  />
                </Box>
              )}
              {!isSubAreaLoading && userRole === TAX_SUPER_ADMIN && (
                <Box width={"200px"}>

                  <AppSelect
                    height={"40px"}
                    width="200px"
                    id="subArea"
                    name="subArea"
                    onChange={(field: string, value: any) => {
                      if (!value) {
                        mutate(
                          getTaxAssessments(
                            page,
                            "",
                            searchBarValue34,
                            50,
                            institution,
                            city,
                            value,
                            "",
                            "",
                            "",
                            selectedDates[0]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            selectedDates[1]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            taxNature
                          )
                        );
                      }
                      setSubArea(value);
                      mutateSubArea(getSubAreas(1, "", value, institution));
                    }}
                    value={subArea as string}
                    placeholder="Sélectionner une zone"
                    options={subAreas}
                    isMulti={false}
                    isLoading={isSubAreaLoading}
                  />
                </Box>
              )}
              <InputGroup w="200px">
                <Input
                  placeholder={`Rechercher un Débiteur`}
                  value={searchBarValue34}
                  onChange={(event: any) =>
                    setSearchBarValue34(event.currentTarget.value)
                  }
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
              <IconButton
                onClick={handleSearch}
                ml={2}
                borderRadius={"10px"}
                py={"19px"}
                border={"1px solid #e2e4e6"}
                aria-label="search"
                icon={<SearchIcon />}
                justifyContent={"center"}
                alignItems={"center"}
              />
            </Flex>
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={tax_assessments}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={
              tax_assessments &&
              tax_assessments.length > 0 &&
              tax_assessments[0].num_of_pages
            }
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche Débiteur</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            <HStack>
              {hasPermission("taxassessment", "update") && <Button
                size="xl"
                variant="outline"
                colorScheme="green_360"
                leftIcon={<EditIcon />}
                fontWeight={700}
                gap="2px"
                borderRadius="8px"
                as={Link}
                href={`/tax-assessments/edit/${activeItem?.id}`}
              >
                Modifier
              </Button>}
              {hasPermission("taxassessment", "delete") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={<DeleteIcon />}
                  fontWeight={700}
                  gap="8px"
                  borderRadius="8px"
                  onClick={() => {
                    onConfirmOpen();
                  }}
                  isDisabled={isDeleting}
                  isLoading={isDeleting}
                >
                  Supprimer
                </Button>
              )}
            </HStack>
            <AppViewListItem label="Reference" value={activeItem?.reference} />
            <AppViewListItem label="Nom" value={activeItem?.tax_payer_name} />
            {activeItem?.amount && (
              <AppViewListItem
                label="Montant de la créance"
                value={activeItem?.amount}
              />
            )}
            <AppViewListItem
              label="Nature de la Créance"
              value={activeItem?.tax_nature_name}
            />
            <AppViewListItem
              label="Date d'échéance"
              value={formatDueDate(activeItem?.due_date)}
            />
            <AppViewListItem label="Zone" value={`${activeItem?.sub_area}`} />
            <AppViewListItem label="Ville" value={`${activeItem?.city}`} />
            <AppViewListItem
              label="Périodicité"
              value={`${activeItem?.tax_frequency}`}
            />
            {activeItem?.rate > 0 && (
              <AppViewListItem
                label="Taux autorisé"
                value={`${activeItem?.rate}%`}
              />
            )}
            <AppViewListItem
              label="Statut"
              value={activeItem?.status === "paid" ? "payé" : "pas à jour"}
            />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer l'évaluation « {activeItem?.reference} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteTaxAssessment(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Évaluation supprimée avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(getTaxAssessments(
                    page,
                    "",
                    searchBarValue34,
                    50,
                    institution,
                    city,
                    subArea,
                    "",
                    "",
                    "",
                    selectedDates[0]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    selectedDates[1]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    taxNature
                  ));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
